import type { FC } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MdOutgoingMail } from 'react-icons/md';
import { Box } from '@mui/material';

import type { FollowUpAction } from 'src/types/myFollowUp';
import type { OrgUnitSimple } from 'src/types/orgUnit';
import useFollowUpDetail from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/FollowUpDetailModal/FollowUpDetailContext';

import { styles } from './index.styles';
import { styles as followUpDetailModalStyles } from 'src/components/myFollowUp/MyFollowUpPage/FollowUpOverview/FollowUpDetailModal/index.styles';

/**
 * Calculate the total headcount across all organizational units, following these rules:
 * 1. Each org unit's headcount is assumed to include the headcount of all its descendants
 * 2. If multiple org units from the same subtree are included, only count the highest one
 *    in the hierarchy to avoid double-counting
 * 3. Sum the headcounts of org units from different subtrees
 */
export const calculateTotalHeadcount = (
  orgUnits: FollowUpAction['orgUnits'] | undefined,
) => {
  if (!orgUnits || orgUnits.length === 0) return 0;

  // Group org units by their paths to identify parent-child relationships.
  const pathGroups = groupOrgUnitsByPath(orgUnits);

  // Sum up the headcounts of each org unit path.
  let totalHeadcount = 0;
  for (const orgUnit of pathGroups) {
    // Note: the "create followup" screen and the followup detail screen for existing
    //  items look identical, but their source for the org units is actually different.
    //  The following works as long as both call the field "headcount".
    totalHeadcount += orgUnit.headcount || 0;
  }

  return totalHeadcount;
};

/**
 * Analyze org unit paths to determine which org units to include in the headcount calculation.
 *
 * This implementation avoids backtracking by sorting paths by length first,
 * ensuring ancestors are processed before descendants.
 */
const groupOrgUnitsByPath = (
  orgUnits: FollowUpAction['orgUnits'],
): OrgUnitSimple[] => {
  // Sort paths by length (shortest to longest) to process ancestors before descendants.
  const sortedPaths = [...orgUnits].sort((a, b) => a.length - b.length);
  // Track which org units should be counted.
  const includedOrgUnits = new Map<number, OrgUnitSimple>();
  // Track which org unit IDs are in the ancestry of included org units.
  const ancestryIds = new Set<number>();

  // Process each org unit path in order of increasing depth.
  for (const path of sortedPaths) {
    const leafOrgUnit = path[path.length - 1];

    // Check if leafOrgUnit is a descendant of any already included org unit.
    const isDescendant =
      path.slice(0, -1).some((ancestor) => includedOrgUnits.has(ancestor.id)) ||
      ancestryIds.has(leafOrgUnit.id);

    if (isDescendant) continue;

    // Add this org unit to the included set.
    includedOrgUnits.set(leafOrgUnit.id, leafOrgUnit);

    // Add all ancestors of this org unit to the ancestry set.
    path.slice(0, -1).forEach((orgUnit) => ancestryIds.add(orgUnit.id));
  }

  return Array.from(includedOrgUnits.values());
};

const HeadcountPill: FC = () => {
  const { actionItem, loading } = useFollowUpDetail();
  const { t } = useTranslation('myFollowUp');

  const totalHeadcount = useMemo(
    () => calculateTotalHeadcount(actionItem?.orgUnits),
    [actionItem?.orgUnits],
  );

  if (loading || !actionItem) return null;

  return (
    <Box sx={[followUpDetailModalStyles.pill, styles.goldenPill]}>
      <MdOutgoingMail /> {t('orgUnit.invited')}: {totalHeadcount}
    </Box>
  );
};

export default HeadcountPill;
